import {
  MAX_LOGGED_MINUTES,
  type Balance,
  type WorkSessionResponse,
} from '@gamble-chess/contracts';
import { useEffect, useState, type JSX, type SubmitEvent } from 'react';

import { useLedger } from '../api/stakes.ts';
import { useDiscardWork, useRecordWork, useStopWork, useWork } from '../api/work.ts';
import {
  counterpartyKey,
  counterpartyRef,
  describeBalance,
  describeWorkTarget,
  formatElapsed,
} from '../format.ts';
import { Alert } from '../ui/Alert.tsx';
import { Card } from '../ui/Card.tsx';
import { Field } from '../ui/Field.tsx';
import { Loaded } from '../ui/Loaded.tsx';
import {
  hintClass,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
  submitClass,
} from '../ui/styles.ts';
import { WorkLog } from './WorkLog.tsx';

/** Only minutes can be worked off, and only what is still owed. */
function workable(balances: readonly Balance[]): readonly Balance[] {
  return balances.filter((balance) => balance.unit === 'minutes' && balance.amount > 0);
}

function RunningSession({ session }: { readonly session: WorkSessionResponse }): JSX.Element {
  const stop = useStopWork();
  const discard = useDiscardWork();
  const [elapsed, setElapsed] = useState(() => Date.now() - Date.parse(session.startedAt));

  useEffect(() => {
    const started = Date.parse(session.startedAt);
    const tick = setInterval(() => {
      setElapsed(Date.now() - started);
    }, 1000);
    return () => {
      clearInterval(tick);
    };
  }, [session.startedAt]);

  const busy = stop.isPending || discard.isPending;
  const error = stop.isError ? stop.error.message : discard.error?.message;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-accent/40 bg-accent-soft px-4 py-3">
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <strong data-testid="work-elapsed" className="text-3xl font-semibold tabular-nums">
          {formatElapsed(elapsed)}
        </strong>
        <span>Working off {describeWorkTarget(session.counterparty)}</span>
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            stop.mutate(session.id);
          }}
          disabled={busy}
          className={primaryButtonClass}
        >
          {stop.isPending ? 'Stopping…' : 'Stop and count it'}
        </button>
        <button
          type="button"
          onClick={() => {
            discard.mutate(session.id);
          }}
          disabled={busy}
          className={secondaryButtonClass}
        >
          Discard
        </button>
      </div>
      {error === undefined ? null : <Alert>{error}</Alert>}
    </div>
  );
}

function StartWorkForm({ balances }: { readonly balances: readonly Balance[] }): JSX.Element {
  const record = useRecordWork();
  const [target, setTarget] = useState('');
  const [minutes, setMinutes] = useState('');

  const chosen = balances.find((balance) => counterpartyKey(balance.counterparty) === target);
  const balance = chosen ?? balances[0];
  if (balance === undefined) {
    return (
      <p className={hintClass}>
        Nothing owed in minutes. Losses under a standing rule and settled wagers give you something
        to work off.
      </p>
    );
  }

  function submit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (balance === undefined) {
      return;
    }
    record.mutate(
      {
        counterparty: counterpartyRef(balance.counterparty),
        minutes: minutes === '' ? null : Number(minutes),
      },
      {
        onSuccess: () => {
          setMinutes('');
        },
      },
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" aria-label="Work off debt">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="What you are working off">
          {(id) => (
            <select
              id={id}
              value={counterpartyKey(balance.counterparty)}
              onChange={(event) => {
                setTarget(event.target.value);
              }}
              className={inputClass}
            >
              {balances.map((option) => (
                <option
                  key={counterpartyKey(option.counterparty)}
                  value={counterpartyKey(option.counterparty)}
                >
                  {describeBalance(option)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field
          label="Minutes already worked"
          hint="Leave it empty to start the timer now. Only what you still owe counts."
        >
          {(id) => (
            <input
              id={id}
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_LOGGED_MINUTES}
              step={1}
              placeholder="Timer"
              value={minutes}
              onChange={(event) => {
                setMinutes(event.target.value);
              }}
              className={inputClass}
            />
          )}
        </Field>
      </div>
      <div>
        <button type="submit" disabled={record.isPending} className={submitClass}>
          {minutes === '' ? 'Start timer' : 'Log minutes'}
        </button>
      </div>
      {record.isError ? <Alert>{record.error.message}</Alert> : null}
    </form>
  );
}

/** The timer and the logging form, plus the last few sessions as a receipt. */
export function WorkItOff(): JSX.Element {
  const work = useWork();
  const ledger = useLedger();

  return (
    <Card
      title="Work it off"
      description="Time worked counts against one balance, up to what is open."
    >
      <Loaded
        query={work}
        loading="Loading your work…"
        error="Your work could not be loaded. Please reload the page."
      >
        {({ running, recent }) => (
          <>
            {running === null ? (
              <Loaded
                query={ledger}
                loading="Loading your balances…"
                error="Your balances could not be loaded. Please reload the page."
              >
                {({ balances }) => <StartWorkForm balances={workable(balances)} />}
              </Loaded>
            ) : (
              <RunningSession session={running} />
            )}
            <WorkLog sessions={recent} limit={3} caption="Recent work" />
          </>
        )}
      </Loaded>
    </Card>
  );
}
